import { useEffect, useMemo, useState } from 'react';
import type { ProjectSearchResult } from '@/api/planning';
import type { VendorPriceRow } from '@/api/onderaannemerPrijzen';
import type { TierBand } from '@/api/onderaannemers';
import type { AddCollectorLine } from '@/api/onderaannemersPoEdit';
import { fetchCollectoren } from '@/api/tasks';
import type { Collector } from '@/types/odoo';
import { AddLineModalShell } from './AddLineModalShell';
import { ProjectPicker } from './ProjectPicker';
import { tierAvailability, priceForTier } from './tierLogic';
import { isManualPricedSystem, useKringSystems, useSystemsStore } from '@/stores/systems';

// A system is "priced" when it has its own Plaatsing-onderaannemers variants, i.e. rows
// in the /prices grid; anything else is manual-priced and needs a typed €/m² (ADR-060).
// Both the roster and the priced flag come from the Odoo-backed registry, so a system
// that gets prices in Odoo starts behaving as priced here without a code change.
// Betonnet bills via staalnet on the backend, but from this UI it is just a picked name.

/** Map a collector's raw systeem string onto a roster code, or null if unrecognised. */
function systeemKeyFrom(raw: string | false, codes: Set<string>): string | null {
  if (!raw) return null;
  const key = raw.toLowerCase().trim().replace(/\s+/g, '');
  return codes.has(key) ? key : null;
}

// "Collector 5" → "C5"; else the raw name. Label shown in the collector picker.
function collectorPickLabel(c: Collector): string {
  const m = c.name.match(/(\d+)\s*$/);
  const short = m ? `C${m[1]}` : c.name;
  const blok = Array.isArray(c.x_studio_blok) ? c.x_studio_blok[1] : '';
  const verd = typeof c.x_studio_verdieping === 'string' ? c.x_studio_verdieping : '';
  const loc = [blok, verd].filter(Boolean).join('/');
  const m2 = c.x_studio_collector_m2 ? `${c.x_studio_collector_m2}m²` : '';
  const sys = typeof c.x_studio_collector_systeem === 'string' ? c.x_studio_collector_systeem : '';
  return [short, loc, m2, sys].filter(Boolean).join(' · ');
}

function parseDecimal(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return null;
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 100) / 100;
  return rounded > 0 ? rounded : null;
}

const euro = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' });

interface AddCollectorModalProps {
  vendorName: string;
  // The PO vendor's price rows (already filtered client-side to this vendor).
  vendorPriceRows: VendorPriceRow[];
  onAdd: (payload: AddCollectorLine) => Promise<void>;
  onClose: () => void;
}

export function AddCollectorModal({ vendorName, vendorPriceRows, onAdd, onClose }: AddCollectorModalProps) {
  // Systeem options + the priced/manual split come from the Odoo-defined roster.
  // Priced systems first, then manual ones (sort is stable, so roster order is kept).
  const rosterSystems = useKringSystems();
  const rosterLoaded = useSystemsStore((s) => s.loaded);
  const systeemOptions = useMemo(
    () => [...rosterSystems].sort((a, b) => Number(b.priced) - Number(a.priced)),
    [rosterSystems],
  );
  const systeemCodes = useMemo(() => new Set(rosterSystems.map((s) => s.code)), [rosterSystems]);
  const [project, setProject] = useState<ProjectSearchResult | null>(null);
  const [label, setLabel] = useState('');
  const [systeem, setSysteem] = useState<string>('tacker');
  const [legpatroon, setLegpatroon] = useState<number>(10);
  const [m2, setM2] = useState('');
  const [tier, setTier] = useState<TierBand>('under200');
  const [priceInput, setPriceInput] = useState('');
  const [priceEdited, setPriceEdited] = useState(false);
  const [collectorId, setCollectorId] = useState('');
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [collectorsLoading, setCollectorsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the chosen project's collectors so the user picks one from a list (not an id).
  useEffect(() => {
    setCollectorId('');
    setCollectors([]);
    if (!project) return;
    let cancelled = false;
    setCollectorsLoading(true);
    fetchCollectoren(project.id)
      .then((res) => { if (!cancelled) setCollectors(res.collectors); })
      .catch(() => { if (!cancelled) setCollectors([]); })
      .finally(() => { if (!cancelled) setCollectorsLoading(false); });
    return () => { cancelled = true; };
  }, [project]);

  // Picking a collector links it and prefills systeem / legpatroon / m² from its data.
  const handleCollectorPick = (idStr: string) => {
    setCollectorId(idStr);
    const picked = collectors.find((c) => String(c.id) === idStr);
    if (!picked) return;
    const sys = systeemKeyFrom(picked.x_studio_collector_systeem, systeemCodes);
    if (sys) { setSysteem(sys); setPriceEdited(false); }
    if (picked.x_studio_collector_m2) setM2(String(picked.x_studio_collector_m2).replace('.', ','));
    const lps = [...new Set((picked.kringen ?? []).map((k) => k.x_legpatroon).filter((n): n is number => Number.isFinite(n) && n > 0))];
    if (lps.length === 1) { setLegpatroon(lps[0]); setPriceEdited(false); }
  };

  // Until the roster has loaded we cannot tell priced from manual, so the modal keeps
  // the priced behaviour and blocks submit (below) rather than asking for a €/m² that
  // the backend would then ignore.
  const manual = rosterLoaded && isManualPricedSystem(systeem);

  // Legpatronen available for the priced system from the vendor grid; fall back to the
  // standard set so the select is never empty even if a vendor hasn't priced everything.
  const legpatronen = useMemo(() => {
    if (manual) return [10, 15, 20, 25, 30];
    const fromGrid = vendorPriceRows
      .filter((r) => r.systeem === systeem)
      .map((r) => r.legpatroon);
    const set = new Set<number>([...fromGrid, 10, 15, 20, 25, 30]);
    return [...set].sort((a, b) => a - b);
  }, [manual, vendorPriceRows, systeem]);

  const priceRow = useMemo(
    () => (manual ? null : vendorPriceRows.find((r) => r.systeem === systeem && r.legpatroon === legpatroon) ?? null),
    [manual, vendorPriceRows, systeem, legpatroon],
  );

  const avail = tierAvailability(priceRow);

  // Effective tier respects availability: if the chosen tier isn't priced, fall to default.
  const effectiveTier: TierBand = manual
    ? 'under200'
    : (tier === 'from200' && !avail.hasFrom200) || (tier === 'under200' && !avail.hasUnder200)
      ? avail.defaultTier
      : tier;

  const gridPrice = manual ? null : priceForTier(priceRow, effectiveTier);
  // Prefill the price from the grid unless the user typed their own.
  const shownPrice = priceEdited ? priceInput : gridPrice != null ? String(gridPrice).replace('.', ',') : priceInput;

  const parsedPrice = parseDecimal(shownPrice);
  // Manual systems require a typed positive price; priced systems can rely on the grid.
  const priceOk = manual ? parsedPrice != null : gridPrice != null || parsedPrice != null;
  const m2Ok = parseDecimal(m2) != null;
  const canSubmit = rosterLoaded && !!project && m2Ok && priceOk;

  const handleSubmit = async () => {
    if (!canSubmit || !project || submitting) return;
    const parsedM2 = parseDecimal(m2)!;
    setSubmitting(true);
    setError(null);
    try {
      const payload: AddCollectorLine = {
        kind: 'collector',
        projectId: project.id,
        systeem,
        legpatroon,
        m2: parsedM2,
      };
      const trimmedLabel = label.trim();
      if (trimmedLabel) payload.label = trimmedLabel;
      if (!manual) payload.tier = effectiveTier;
      // Send an override only when the user typed a price that differs from the grid, or
      // there's no grid price (manual systems always send it).
      if (manual) {
        if (parsedPrice != null) payload.priceOverride = parsedPrice;
      } else if (priceEdited && parsedPrice != null && parsedPrice !== gridPrice) {
        payload.priceOverride = parsedPrice;
      }
      const cid = Number(collectorId.trim());
      if (collectorId.trim() && Number.isFinite(cid) && cid > 0) payload.collectorId = cid;
      await onAdd(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Collector toevoegen mislukt');
      setSubmitting(false);
    }
  };

  return (
    <AddLineModalShell
      title="Collector toevoegen"
      submitLabel="Toevoegen"
      submitting={submitting}
      canSubmit={canSubmit}
      error={error}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div>
        <label className="block text-sm font-semibold text-td-navy mb-1">Project</label>
        <ProjectPicker selected={project} onSelect={setProject} autoFocus />
      </div>

      <div>
        <label htmlFor="add-col-label" className="block text-sm font-semibold text-td-navy mb-1">
          Omschrijving <span className="font-normal text-td-muted">(optioneel)</span>
        </label>
        <input
          id="add-col-label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Bijv. C3 · Blok A/+01"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-td-navy focus:outline-none focus:ring-2 focus:ring-td-blue"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="add-col-systeem" className="block text-sm font-semibold text-td-navy mb-1">Systeem</label>
          <select
            id="add-col-systeem"
            value={systeem}
            onChange={(e) => { setSysteem(e.target.value); setPriceEdited(false); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-td-blue"
          >
            {!systeemCodes.has(systeem) && <option value={systeem}>{systeem}</option>}
            {systeemOptions.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="add-col-lp" className="block text-sm font-semibold text-td-navy mb-1">Legpatroon</label>
          <select
            id="add-col-lp"
            value={legpatroon}
            onChange={(e) => { setLegpatroon(Number(e.target.value)); setPriceEdited(false); }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-td-blue"
          >
            {legpatronen.map((lp) => (
              <option key={lp} value={lp}>LP {lp}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="add-col-m2" className="block text-sm font-semibold text-td-navy mb-1">m²</label>
          <input
            id="add-col-m2"
            type="text"
            inputMode="decimal"
            value={m2}
            onChange={(e) => setM2(e.target.value)}
            placeholder="0,00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
          />
        </div>
        <div>
          <label htmlFor="add-col-price" className="block text-sm font-semibold text-td-navy mb-1">
            €/m² {manual && <span className="text-red-500">*</span>}
          </label>
          <input
            id="add-col-price"
            type="text"
            inputMode="decimal"
            value={shownPrice}
            onChange={(e) => { setPriceInput(e.target.value); setPriceEdited(true); }}
            placeholder={manual ? 'verplicht' : '0,00'}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-td-blue"
          />
        </div>
      </div>

      {/* Tier toggle — only when the vendor prices both tiers for this product. */}
      {!manual && avail.showToggle && (
        <div>
          <span className="block text-sm font-semibold text-td-navy mb-1">Staffel</span>
          <div className="inline-flex rounded-full bg-gray-100 p-0.5" role="group" aria-label="Staffel kiezen">
            {(['under200', 'from200'] as const).map((band) => {
              const active = effectiveTier === band;
              return (
                <button
                  key={band}
                  type="button"
                  onClick={() => { setTier(band); setPriceEdited(false); }}
                  aria-pressed={active}
                  className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                    active ? 'bg-white text-td-navy shadow-sm' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {band === 'from200' ? '200+ tarief' : 'Basistarief'}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!manual && avail.singleRate && (
        <p className="text-xs font-medium text-td-blue">Vast tarief ({gridPrice != null ? euro.format(gridPrice) : '—'}/m²)</p>
      )}
      {!manual && !avail.hasAnyPrice && (
        <p className="text-xs text-amber-700">
          Geen vaste prijs voor {vendorName} bij dit legpatroon — vul een €/m² in.
        </p>
      )}

      <div>
        <label htmlFor="add-col-cid" className="block text-sm font-semibold text-td-navy mb-1">
          Koppel aan collector <span className="font-normal text-td-muted">(optioneel)</span>
        </label>
        <select
          id="add-col-cid"
          value={collectorId}
          onChange={(e) => handleCollectorPick(e.target.value)}
          disabled={!project || collectorsLoading}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-td-blue disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {!project
              ? 'Kies eerst een project'
              : collectorsLoading
                ? 'Collectoren laden…'
                : collectors.length === 0
                  ? 'Geen collectoren in dit project'
                  : '— Geen (handmatige regel) —'}
          </option>
          {collectors.map((c) => (
            <option key={c.id} value={c.id}>{collectorPickLabel(c)}</option>
          ))}
        </select>
        {collectorId && (
          <p className="mt-1 text-xs text-td-muted">Systeem, legpatroon en m² zijn vooringevuld — pas aan indien nodig.</p>
        )}
      </div>
    </AddLineModalShell>
  );
}
